import axios from "axios";
import type {
  Branding,
  CatalogCollection,
  ClientConfig,
  DetectedObject,
  ExtractionStatus,
  IntegrateRequest,
  IntegrateResult,
  LeadSubmission,
  PairingSlot,
  PostcardLandingPayload,
  PostcardQuoteSubmission,
  PriceQuote,
  RenderRequest,
  RenderResult,
  ResolveAddressPhotoResult,
  ResolveResult,
  SampleHome,
  TopProfile,
  UploadTicket,
} from "./types";

const http = axios.create({ baseURL: "/api/v1" });

// The scope token may be composite ("manufacturer/amarr") — encode per segment so the path
// separator survives and the request hits the manufacturer-scoped API route.
const base = (slug: string) =>
  `/visualizer/${slug.split("/").map(encodeURIComponent).join("/")}`;

// Attribute writes to the analytics session the host created at extraction time.
const sessionHeader = (sessionId?: string | null) =>
  sessionId ? { headers: { "X-Visualizer-Session": sessionId } } : undefined;

/**
 * Canonicalize a detection response to lowercase enum values so app logic never depends on the host's enum
 * casing. This host emits camelCase already (so it's a no-op today), but it keeps detection processing
 * identical to the proposal SPA — which normalizes the platform host's PascalCase at this same boundary —
 * and guards against a future serializer change silently dropping an Angled/Arched opening to flat.
 */
function normalizeExtraction(s: ExtractionStatus): ExtractionStatus {
  return {
    ...s,
    objects: (s.objects ?? []).map((o) => ({
      ...o,
      doorType: o.doorType ? (o.doorType.toLowerCase() as DetectedObject["doorType"]) : o.doorType,
      topProfile: o.topProfile
        ? { ...o.topProfile, kind: o.topProfile.kind.toLowerCase() as TopProfile["kind"] }
        : o.topProfile,
    })),
  };
}

// Showroom mode → ask the server for the full active catalogue (all enabled manufacturers/collections,
// regardless of pricing) and price-off branding. Absent flag = the default priced experience.
const catalogueParams = (opts?: { full?: boolean }) =>
  opts?.full ? { params: { catalogue: "full" } } : undefined;

export interface ResolveParams {
  collectionId: string;
  designId?: string;
  colorId?: string;
  windowOptionId?: string;
  glassTypeId?: string;
  hardwareId?: string;
  colorGroup?: string;
  width?: number;
  height?: number;
}

export interface PriceParams {
  collectionId: string;
  designId?: string;
  colorId?: string;
  colorGroup?: string;
  windowOptionId?: string;
  windowGroup?: string;
  hardwareId?: string;
  // Chosen insulation model — its dealer upcharge is folded into the returned figures.
  constructionId?: string;
  // true → headline/"From" price reflects the single-car (8×7) column.
  isSingle?: boolean;
}

export const api = {
  getBranding: (slug: string, opts?: { full?: boolean }) =>
    http.get<Branding>(`${base(slug)}/branding`, catalogueParams(opts)).then((r) => r.data),

  getCollections: (slug: string, opts?: { full?: boolean }) =>
    http.get<CatalogCollection[]>(`${base(slug)}/catalog/collections`, catalogueParams(opts)).then((r) => r.data),

  // Sample home photos for the "no photo handy? try a sample" row (global starters + the dealer's own).
  getSamples: (slug: string) =>
    http.get<SampleHome[]>(`${base(slug)}/samples`).then((r) => r.data),

  resolve: (slug: string, params: ResolveParams) =>
    http.get<ResolveResult>(`${base(slug)}/catalog/resolve`, { params }).then((r) => r.data),

  // Prices the current selection against the dealer's rules; null when no rule matches.
  price: (slug: string, params: PriceParams) =>
    http
      .get<{ quote: PriceQuote | null }>(`${base(slug)}/catalog/price`, { params })
      .then((r) => r.data.quote),

  createPairing: (slug: string) =>
    http.post<PairingSlot>(`${base(slug)}/pair`).then((r) => r.data),

  getPairing: (slug: string, pairingId: string) =>
    http.get<PairingSlot>(`${base(slug)}/pair/${pairingId}`).then((r) => r.data),

  completePairing: (slug: string, pairingId: string, photoUrl: string) =>
    http.post(`${base(slug)}/pair/${pairingId}/complete`, { photoUrl }),

  createUploadTicket: (slug: string, file: File) =>
    http
      .post<UploadTicket>(`${base(slug)}/uploads/ticket`, {
        fileName: file.name,
        contentType: file.type || "image/jpeg",
        sizeBytes: file.size,
      })
      .then((r) => r.data),

  uploadToTicket: (ticket: UploadTicket, file: File) =>
    axios.put(ticket.uploadUrl, file, { headers: { "Content-Type": ticket.contentType } }),

  startExtract: (slug: string, photoUrl: string) =>
    http
      .post<{ jobId: string; sessionId: string | null }>(`${base(slug)}/extract-geometry`, { photoUrl })
      .then((r) => r.data),

  // Resolves a home photo for a street address (DoorLeadScore Street View pipeline) to feed the upload flow.
  resolveAddressPhoto: (slug: string, address: string) =>
    http
      .post<ResolveAddressPhotoResult>(`${base(slug)}/resolve-address-photo`, { address })
      .then((r) => r.data),

  getExtract: (slug: string, jobId: string) =>
    http.get<ExtractionStatus>(`${base(slug)}/extract-geometry/${jobId}`).then((r) => normalizeExtraction(r.data)),

  render: (slug: string, req: RenderRequest, sessionId?: string | null) =>
    http.post<RenderResult>(`${base(slug)}/render`, req, sessionHeader(sessionId)).then((r) => r.data),

  integrate: (slug: string, req: IntegrateRequest, sessionId?: string | null) =>
    http.post<IntegrateResult>(`${base(slug)}/integrate`, req, sessionHeader(sessionId)).then((r) => r.data),

  submitLead: (slug: string, lead: LeadSubmission, sessionId?: string | null) =>
    http.post(`${base(slug)}/leads`, lead, sessionHeader(sessionId)),

  // Postcard QR landing: the code personalizes the session (photo + detections + door presets).
  // 404 = the branded "link expired" card, not an error state.
  getPostcardLanding: (slug: string, code: string) =>
    http
      .get<PostcardLandingPayload>(`${base(slug)}/postcards/${encodeURIComponent(code)}`)
      .then((r) => r.data),

  // Quote from a postcard session — written onto the dealer's known lead (identity = the code).
  submitPostcardQuote: (slug: string, code: string, quote: PostcardQuoteSubmission) =>
    http.post(`${base(slug)}/postcards/${encodeURIComponent(code)}/quote`, quote),
};

// App-wide (not tenant-scoped) client config — ships the browser Google Maps key for address autocomplete.
export const configApi = {
  get: () => http.get<ClientConfig>("/config").then((r) => r.data),
};
